import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    /*
     * Luister ook naar toevoegingen vanuit de WSV live search.
     *
     * De zoekfunctie gebruikt Shopify AJAX (/cart/add.js).
     * Het event wordt op window verstuurd zodat het ook werkt
     * wanneer de zoekfunctie in een Shadow DOM staat.
     */
    window.addEventListener(
      'wsv:cart-added',
      this.onWsvCartAdded
    );

    window.addEventListener(
      'pageshow',
      this.onPageShow
    );

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    /*
     * Verwijder ook onze WSV AJAX listener.
     */
    window.removeEventListener(
      'wsv:cart-added',
      this.onWsvCartAdded
    );

    window.removeEventListener(
      'pageshow',
      this.onPageShow
    );
  }

  /**
   * Handles an AJAX cart update coming from the WSV live search.
   *
   * Shopify's real cart.item_count is used.
   *
   * Voorbeeld:
   * 5 bestaande items + 2 toegevoegd = 7
   * Daarna +1 = 8
   *
   * Er wordt GEEN pagina-refresh uitgevoerd.
   *
   * @param {CustomEvent} event - WSV cart event.
   */
  onWsvCartAdded = (event) => {
    const cart = event?.detail?.cart;

    const itemCount =
      cart?.item_count ??
      event?.detail?.itemCount;

    /*
     * Alleen doorgaan als Shopify daadwerkelijk
     * een geldig aantal heeft teruggegeven.
     */
    if (typeof itemCount !== 'number') {
      return;
    }

    /*
     * Gebruik de bestaande Shopify cart-bubble renderer.
     *
     * Hierdoor blijven:
     * - het bolletje
     * - tonen/verbergen
     * - de cart-animatie
     * - sessionStorage
     *
     * allemaal hetzelfde werken als normaal.
     */
    this.renderCartBubble(itemCount);
  };

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount =
          cart?.totalQuantity ??
          detail?.itemCount ??
          0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-icon] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Renders the cart bubble.
   *
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (
    itemCount,
    animate = true
  ) => {
    /*
     * Toon/verberg het aantal.
     */
    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      itemCount === 0
    );

    /*
     * Toon/verberg het volledige bolletje.
     */
    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      itemCount === 0
    );

    /*
     * Zet het daadwerkelijke aantal.
     *
     * < 100 = exact aantal
     * >= 100 = leeg, zoals het bestaande thema al deed.
     */
    this.currentCartCount = itemCount;

    /*
     * Zorg dat het cart-icoon weet dat er iets in de cart zit.
     */
    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    /*
     * Bewaar het aantal voor de bestaande
     * sessionStorage-controle.
     */
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    /*
     * Geen animatie wanneer de cart leeg is
     * of wanneer animate=false is.
     */
    if (!animate || itemCount === 0) {
      return;
    }

    /*
     * Ensure element is visible before starting animation.
     */
    await new Promise((resolve) =>
      requestAnimationFrame(resolve)
    );

    /*
     * Gebruik de bestaande cart-bubble animatie.
     */
    this.refs.cartBubble.classList.add(
      'cart-bubble--animating'
    );

    await onAnimationEnd(
      this.refs.cartBubbleText
    );

    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    /*
     * Ensure refs are available.
     */
    if (!this.refs.cartBubbleCount) {
      return;
    }

    const sessionStorageCount =
      sessionStorage.getItem('cart-count');

    /*
     * If no session storage data, nothing to check.
     */
    if (sessionStorageCount === null) {
      return;
    }

    const visibleCount =
      this.refs.cartBubbleCount.textContent;

    try {
      const {
        value,
        timestamp
      } = JSON.parse(
        sessionStorageCount
      );

      /*
       * Als het al klopt, niets doen.
       */
      if (value === visibleCount) {
        return;
      }

      /*
       * Alleen recente waarden gebruiken.
       */
      if (
        Date.now() - timestamp <
        10000
      ) {
        const count =
          parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(
            count,
            false
          );
        }
      }
    } catch (_) {
      /*
       * Ongeldige sessionStorage:
       * niets doen.
       */
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define(
    'cart-icon',
    CartIcon
  );
}